import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Zap, Lock, Infinity as InfinityIcon } from "lucide-react";
import BookCover from "@/components/landing/BookCover";

const EASE = [0.16, 1, 0.3, 1];

const PRODUCTS = [
  {
    id: "memorisation",
    covers: ["mem"],
    badge: null,
    title: "Guide de mémorisation",
    sub: "En 6 mois",
    desc: "Un plan jour par jour pour mémoriser le Coran en 6 mois, sans te sentir dépassé.",
    steps: ["Planification structurée", "Techniques éprouvées", "Suivi de progression", "Motivation spirituelle"],
    price: "29,99 €",
    cta: "Commencer",
    bundle: false,
  },
  {
    id: "revision",
    covers: ["rev"],
    badge: null,
    title: "Guide de révision",
    sub: "En 30 jours",
    desc: "Un planning de 30 jours pour consolider ce que tu as déjà mémorisé, durablement.",
    steps: ["Planning jour par jour", "Techniques de révision", "Suivi de progression", "Conseils pratiques"],
    price: "24,99 €",
    cta: "Réviser",
    bundle: false,
  },
  {
    id: "pack-complet",
    covers: ["mem", "rev"],
    badge: "Le plus choisi",
    title: "Pack Gold",
    sub: "Mémorisation + révision",
    desc: "Le parcours complet, du premier verset à la consolidation durable : 6 mois pour mémoriser pas à pas, puis 30 jours pour ancrer durablement chaque verset appris — avec accès à vie et 9,99 € d'économie.",
    steps: ["Les 2 guides réunis", "Un parcours de bout en bout", "Accès à vie", "9,99 € d'économie"],
    oldPrice: "54,98 €",
    price: "44,99 €",
    cta: "Tout obtenir",
    bundle: true,
  },
];

const ASSURANCES = [
  { icon: Zap, label: "Livraison instantanée" },
  { icon: Lock, label: "Paiement sécurisé" },
  { icon: InfinityIcon, label: "Accès à vie" },
];

export default function GuidesPage() {
  return (
    <div className="info-page" data-testid="guides-page">
      <header className="info-header">
        <Link to="/" className="info-back" data-testid="back-home-link">
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>
        <Link to="/" className="logo logo-link" data-testid="brand-home-link" aria-label="SamNara — accueil"><img src="/logo-samnara.png" alt="SamNara" className="logo-img logo-img-lg" /></Link>
      </header>

      <div className="container">
        <motion.div
          className="info-hero"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <div className="eyebrow">La collection</div>
          <h1>Les guides disponibles</h1>
          <p className="guides-lede">
            Deux guides numériques et un Pack Gold — livrés instantanément après achat, accessibles à vie.
          </p>
        </motion.div>
      </div>

      <section className="guides-band" data-testid="guides-products">
        <div className="container">
          <div className="products-grid">
            {PRODUCTS.map((p, i) => (
              <motion.article
                key={p.id}
                className={`pack-card product-card ${p.bundle ? "bundle" : ""}`}
                initial={{ opacity: 0, y: 70 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1, delay: i * 0.12, ease: EASE }}
                whileHover={{ y: -8 }}
                data-testid={`product-card-${p.id}`}
              >
                {p.badge && <div className="badge-gold" data-testid={`product-badge-${p.id}`}>{p.badge}</div>}
                <div className="pack-cover-clip product-covers">
                  {p.covers.map((c) => (
                    <BookCover key={c} variant={c} mini />
                  ))}
                </div>
                <h3>{p.title}</h3>
                <div className="sub">{p.sub}</div>
                <p className="product-desc">{p.desc}</p>
                <ul className="steps">
                  {p.steps.map((s, j) => (
                    <li key={j}>
                      <span className="step-num">{j + 1}</span>
                      {s}
                    </li>
                  ))}
                </ul>
                {p.oldPrice && <div className="old-price">{p.oldPrice}</div>}
                <div className="price" data-testid={`product-price-${p.id}`}>{p.price}</div>
                <Link
                  to={`/commander?produit=${p.id}`}
                  className={`btn ${p.bundle ? "btn-light" : ""}`}
                  data-testid={`buy-${p.id}-btn`}
                >
                  {p.cta}
                </Link>
              </motion.article>
            ))}
          </div>

          <motion.div
            className="assure-row"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE }}
            data-testid="guides-assurances"
          >
            {ASSURANCES.map(({ icon: Icon, label }) => (
              <div className="assure-item" key={label}>
                <Icon size={18} />
                <span>{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <footer className="info-footer">
        <div className="copyright">
          © SamNara — Guides numériques, livraison instantanée après achat
        </div>
      </footer>
    </div>
  );
}
